import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";

const GRADE_TONE = { A: "success", B: "success", C: "info", D: "warning", E: "danger", F: "danger" };

const round1 = n => Math.round(n * 10) / 10;

const semesterColumns = [
  { id: "code", header: "Course Code", accessorFn: c => c.course?.code || "—", meta: { variant: "mono" } },
  { id: "name", header: "Course Name", accessorFn: c => c.course?.name || "—" },
  { id: "credits", header: "Credits", accessorFn: c => c.course?.creditUnits || "—" },
  {
    id: "cw",
    header: "CW (/60)",
    accessorFn: c => {
      const r = c.result;
      if (!r) return "—";
      return round1((r.ca1Score || 0) * 0.3 + (r.ca2Score || 0) * 0.2 + (r.practicalScore || 0) * 0.1);
    }
  },
  { id: "exam", header: "Exam (/40)", accessorFn: c => (c.result ? round1((c.result.examScore || 0) * 0.4) : "—") },
  { id: "total", header: "Total", accessorFn: c => (c.result?.totalScore != null ? `${c.result.totalScore}%` : "—") },
  {
    id: "grade",
    header: "Grade",
    cell: info => {
      const grade = info.row.original.result?.grade || "—";
      return grade !== "—" ? <Pill tone={GRADE_TONE[grade] || "muted"}>{grade}</Pill> : "—";
    }
  }
];

const styles = {
  letterhead: { textAlign: "center", borderBottom: "2px solid #1a237e", paddingBottom: "12px", marginBottom: "16px" },
  summaryBox: { background: "#f5f5f5", borderRadius: "8px", padding: "16px", marginTop: "16px" },
  signBlock: { display: "flex", justifyContent: "space-between", marginTop: "48px" },
  signLine: { borderTop: "1px solid #333", paddingTop: "6px", width: "220px", fontSize: "12px", color: "#555" },
  stampBox: {
    width: "120px", height: "120px", border: "1px dashed #999", borderRadius: "8px",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "11px", color: "#999", textAlign: "center"
  }
};

class PortalStatementOfResults extends Component {
  state = { data: null, loading: true, error: null };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    try {
      const res = await api.get("/portal/statement");
      const d = res.data.data || res.data;
      this.setState({ data: d, loading: false });
    } catch (e) {
      this.setState({ error: "Could not load your statement of results.", loading: false });
    }
  };

  handlePrint = () => { window.print(); };

  render() {
    const { classes, history } = this.props;
    const { data, loading, error } = this.state;

    if (loading) return <div style={{ textAlign: "center", padding: "100px" }}><CircularProgress /></div>;
    if (error) return <p style={{ color: "#f44336", padding: "20px" }}>{error}</p>;
    if (!data) return null;

    const { student, semesters, cgpa } = data;

    return (
      <GridContainer>
        <style>{`
          @media print {
            body * { visibility: hidden; }
            #portal-statement-printable, #portal-statement-printable * { visibility: visible; }
            #portal-statement-printable { position: absolute; left: 0; top: 0; width: 100%; }
          }
        `}</style>
        <GridItem xs={12}>
          <Card>
            <CardHeader
              color="primary"
              style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <div>
                <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Statement of Results</h4>
                <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                  Official record of your academic performance
                </p>
              </div>
              <Button color="white" simple onClick={() => history.goBack()} style={{ color: "#fff" }}>
                Back
              </Button>
            </CardHeader>
            <CardBody>
              <div id="portal-statement-printable">
              <div className={classes.letterhead}>
                <h3 style={{ margin: 0, color: "#1a237e", fontWeight: "bold" }}>BISHOP WILLIS CORE PTC</h3>
                <p style={{ margin: "2px 0", color: "#666", fontSize: "13px" }}>Iganga, Uganda | P.O. Box XX</p>
                <h4 style={{ margin: "8px 0 0", color: "#333" }}>STATEMENT OF RESULTS</h4>
              </div>

              <GridContainer style={{ marginBottom: "16px" }}>
                <GridItem xs={12} sm={6}>
                  <table style={{ width: "100%", fontSize: "13px" }}>
                    <tbody>
                      {[
                        ["Student Name", `${student.firstName || ""} ${student.lastName || ""}`.trim()],
                        ["Student No.", student.studentNo],
                        ["Programme", student.programme?.name || "—"],
                        ["Year of Study", student.yearOfStudy || "—"]
                      ].map(([l, v]) => (
                        <tr key={l}>
                          <td style={{ fontWeight: "bold", color: "#555", padding: "3px 12px 3px 0", width: "130px" }}>{l}:</td>
                          <td style={{ color: "#333" }}>{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </GridItem>
                <GridItem xs={12} sm={6}>
                  <table style={{ width: "100%", fontSize: "13px" }}>
                    <tbody>
                      {[
                        ["Enrollment Date", student.enrollmentDate || "—"],
                        ["Status", student.status || "—"],
                        ["Date of Issue", new Date().toLocaleDateString("en-UG")]
                      ].map(([l, v]) => (
                        <tr key={l}>
                          <td style={{ fontWeight: "bold", color: "#555", padding: "3px 12px 3px 0", width: "130px" }}>{l}:</td>
                          <td style={{ color: "#333" }}>{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </GridItem>
              </GridContainer>

              {semesters.map(s => (
                <div key={s.semester?.id || Math.random()} style={{ marginBottom: "20px" }}>
                  <p style={{ fontWeight: "bold", color: "#1a237e", borderBottom: "1px solid #e0e0e0", paddingBottom: "4px", marginBottom: "8px" }}>
                    {s.semester?.name || "Unknown Semester"}
                  </p>
                  <DataTable
                    columns={semesterColumns}
                    data={s.courses}
                    emptyState="No results for this semester."
                  />
                </div>
              ))}

              <div className={classes.summaryBox}>
                <GridContainer>
                  <GridItem xs={12} sm={4} style={{ textAlign: "center" }}>
                    <p style={{ fontWeight: "bold", fontSize: "22px", color: "#1a237e", margin: 0 }}>{semesters.length}</p>
                    <p style={{ fontSize: "12px", color: "#666", margin: 0 }}>Semesters</p>
                  </GridItem>
                  <GridItem xs={12} sm={4} style={{ textAlign: "center" }}>
                    <p style={{ fontWeight: "bold", fontSize: "22px", color: "#1a237e", margin: 0 }}>{cgpa}</p>
                    <p style={{ fontSize: "12px", color: "#666", margin: 0 }}>CGPA</p>
                  </GridItem>
                  <GridItem xs={12} sm={4} style={{ textAlign: "center" }}>
                    <p style={{ fontWeight: "bold", fontSize: "22px", color: "#1a237e", margin: 0 }}>
                      {semesters.reduce((sum, s) => sum + s.courses.length, 0)}
                    </p>
                    <p style={{ fontSize: "12px", color: "#666", margin: 0 }}>Courses</p>
                  </GridItem>
                </GridContainer>
              </div>

              <p style={{ fontSize: "11px", color: "#999", marginTop: "16px" }}>
                Grade Scale: A=80-100 (5.0), B=70-79 (4.0), C=60-69 (3.0), D=50-59 (2.0), E=40-49 (1.0), F=0-39 (0.0)
              </p>

              <div className={classes.signBlock}>
                <div className={classes.signLine}>Registrar&apos;s Signature</div>
                <div className={classes.stampBox}>Official Stamp<br />(pending format confirmation)</div>
              </div>
              </div>
            </CardBody>
            <CardFooter style={{ justifyContent: "flex-end", padding: "8px 20px 20px" }}>
              <Button onClick={this.handlePrint} color="primary">
                Print / Save PDF
              </Button>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}

PortalStatementOfResults.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(PortalStatementOfResults));
